<template>
    <v-container fluid class="fill-height">
        <v-row v-if="initialized" class="fill-height" style="min-width: 1200px">
            <!-- LEFT SIDE -->
            <v-col cols="3" class="ml-4" style="min-width: 350px">
                <v-toolbar v-if="request && request.code" flat class="bg-grey-lighten-4">
                    <v-toolbar-title class="text-primary"
                        >Request: {{ request.code }}</v-toolbar-title
                    >
                </v-toolbar>
                <experiments-search
                    v-model="exp"
                    :autofocus="true"
                    class="mt-4"
                ></experiments-search>
                <v-container class="d-flex justify-space-around pl-6 mt-n4">
                    <v-btn
                        v-if="!isMultiAssign"
                        class="ma-1"
                        color="primary"
                        @click="openMenu('assign')"
                        :disabled="!enableAssignButton"
                        >{{ this.shouldCreateSamples ? "Create" : "Assign" }}</v-btn
                    >
                    <v-btn
                        class="ma-1"
                        color="primary"
                        @click="openMenu('edit')"
                        :disabled="
                            !this.exp ||
                            (!selectionHasCondition && sampleEditSelection.length === 0)
                        "
                        >Edit</v-btn
                    >
                    <v-btn
                        class="ma-1"
                        color="primary"
                        @click="remove"
                        :disabled="!selectionHasCondition && sampleEditSelection.length === 0"
                        >Remove</v-btn
                    >
                </v-container>
                <v-label style="font-size: 14px" v-if="exp && !isMultiAssign" for="radio-group">
                    <v-icon> $flask-outline </v-icon>
                    Source Type:
                </v-label>
                <v-radio-group
                    id="radio-group"
                    v-if="exp && !isMultiAssign"
                    v-model="radio"
                    inline
                    mandatory="true"
                    class="pl-4 mt-3"
                    v-on:update:model-value="clearMetadata"
                >
                    <v-radio
                        v-if="!isFermenterRunExperiment"
                        label="Entity"
                        value="condition"
                    ></v-radio>
                    <v-radio
                        v-if="isFermenterRunExperiment"
                        label="Fermenter Batch"
                        value="fermenter"
                    ></v-radio>
                    <v-radio
                        v-if="isFermenterRunExperiment"
                        label="MDA Batch"
                        value="mda"
                    ></v-radio>
                    <v-radio
                        v-if="isFermenterRunExperiment"
                        label="Protein Batch"
                        value="protein"
                    ></v-radio>
                    <v-radio label="Batch (Search)" value="batch"></v-radio>
                    <v-radio
                        v-if="!this.isSampleBool && !isFermenterRunExperiment"
                        label="Sample"
                        value="sample"
                    ></v-radio>
                </v-radio-group>
                <v-divider />

                <conditions-list
                    v-if="exp && radio === 'condition'"
                    :experiment-id="exp.exp"
                    class="mt-5"
                    @openOptionsMenu="onOpenOptionsMenu"
                ></conditions-list>
                <div v-else-if="exp && radio === 'batch'">
                    <v-label class="mt-6 mb-2" style="font-size: 14px">
                        <v-icon>$magnify</v-icon>
                        Single Batch Search:
                    </v-label>
                    <p style="font-size: 14px">
                        Enter a batch identifier (e.g. PEP123-001) to search for a batch.
                    </p>
                    <batch-search class="mb-4" :experiment="exp"></batch-search>
                    <v-divider horizontal />
                    <v-label class="mt-6 mb-2" style="font-size: 14px">
                        <v-icon>$magnify</v-icon>
                        Bulk Batch Search:
                    </v-label>
                    <p style="font-size: 14px" class="mb-2">
                        Enter multiple batch identifiers (e.g. PEP123-001) below, separated by
                        commas or new lines.
                    </p>
                    <div v-if="!isTubeSeries" class="d-flex justify-end">
                        <v-btn
                            color="primary"
                            size="x-small"
                            class="mb-2"
                            :disabled="!hasActiveCondition"
                            @click="openMenu('assignAll')"
                            >Assign All</v-btn
                        >
                    </div>
                    <entity-bulk-search
                        entities-label="Batches"
                        :entity-lookup-fn="fetchBatches"
                        @entitiesFound="onBatchesBulkSelected"
                    ></entity-bulk-search>
                </div>
                <div v-else-if="exp && (radio === 'sample' || isMultiAssign)">
                    <v-label class="mt-6 mb-2" style="font-size: 14px">
                        <v-icon>$magnify</v-icon>
                        Single Sample Search:
                    </v-label>
                    <p style="font-size: 14px">Search for a sample or scan a barcode.</p>
                    <sample-select
                        :experiment-id="exp.exp"
                        class="mt-5"
                        @openOptionsMenu="onOpenOptionsMenu"
                    ></sample-select>
                    <v-divider horizontal />
                    <v-label class="mt-6 mb-2" style="font-size: 14px">
                        <v-icon>$magnify</v-icon>
                        Bulk Sample Search:
                    </v-label>
                    <p style="font-size: 14px" class="mb-2">
                        Enter multiple sample identifiers below, separated by commas or new lines.
                    </p>
                    <div v-if="!isTubeSeries" class="d-flex justify-end">
                        <v-btn
                            color="primary"
                            size="x-small"
                            class="mb-2"
                            :disabled="!hasActiveCondition"
                            @click="openMenu('assignAll')"
                            >Assign All</v-btn
                        >
                    </div>
                    <entity-bulk-search
                        entities-label="Samples"
                        :entity-lookup-fn="fetchSamples"
                        @entitiesFound="onSamplesBulkSelected"
                    ></entity-bulk-search>
                </div>
                <batch-list
                    v-else-if="radio === 'fermenter' || radio === 'mda' || radio === 'protein'"
                    :experiment="exp"
                    :batchType="radio"
                    @batchesSelected="onBatchesSelected"
                    @openOptionsMenu="onOpenOptionsMenu"
                />
                <p v-else class="text-grey pl-8 pt-8">
                    Enter an Experiment Id to assign or edit Conditions, Batches, or Samps
                </p>
            </v-col>
            <!-- RIGHT SIDE - MAIN CONTENT -->
            <v-col class="mt-3 mr-4">
                <v-card>
                    <v-tabs :model-value="activePlate" v-if="!isTubeSeries" grow>
                        <v-tab
                            v-for="(plate, index) in plates"
                            :key="index"
                            @click="setActivePlate(index)"
                        >
                            <div>
                                <span v-if="plate.name">{{ plate.name }}</span>
                                <span v-if="!plate.name">Plate {{ index + 1 }}</span>
                                <span v-if="plate.location">({{ plate.location }})</span>
                            </div>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="black"
                                size="small"
                                variant="text"
                                @click="removeTabDialog(index)"
                            >
                                <v-icon size="small">$window-close</v-icon>
                            </v-btn>
                        </v-tab>
                        <v-btn
                            class="ma-1 ml-3"
                            size="small"
                            variant="text"
                            @click="addTab"
                            v-if="plates.length < 18 && !isMultiAssign"
                        >
                            <v-icon>$plus</v-icon>
                        </v-btn>
                    </v-tabs>
                    <div
                        class="v-window v-item-group theme--light v-tabs-items"
                        v-if="!isTubeSeries"
                    >
                        <div class="v-window__container" style="">
                            <div data-v-1f924702="" class="v-window-item v-window-item--active">
                                <plate-layout
                                    v-if="plates.length > 0"
                                    :modelValue="activePlate"
                                ></plate-layout>
                                <div class="px-4">
                                    <v-alert
                                        v-if="plates.length === 0"
                                        border="start"
                                        text
                                        type="info"
                                    >
                                        {{ noPlatesMessage }}
                                    </v-alert>
                                </div>
                            </div>
                        </div>
                    </div>
                    <TubeSeries v-if="isTubeSeries" v-model="isValid"></TubeSeries>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" variant="text" @click="onCancel">Cancel</v-btn>
                        <v-btn color="primary" @click="onProceed">Next</v-btn>
                    </v-card-actions>
                </v-card>
                <v-row>
                    <v-col class="pt-5 pr-1">
                        <experiment-card :experiment="exp"></experiment-card>
                    </v-col>
                    <v-col class="pt-5 pl-1">
                        <container-card></container-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <create-request-dialog
            v-if="showCreateRequestDialog && !initialized"
            @submit="onInitialize"
        ></create-request-dialog>
        <create-samples-dialog
            v-if="showCreateSamplesDialog && !initialized"
            @submit="onInitialize"
        ></create-samples-dialog>
        <submit-dialog
            v-model="isSubmitDialogOpen"
            @submit="onSubmit"
            :error-message="errorMessage"
        ></submit-dialog>

        <!-- Remove Tab confirmation box -->
        <v-dialog v-model="showRemoveTabConfirmDialog" max-width="290">
            <v-card>
                <v-card-text class="text-body-1 py-3">
                    Are you sure you want to close Tab {{ removeTabIndex + 1 }}
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey"
                        variant="text"
                        @click="confirmRemoveTab(removeTabIndex, false)"
                    >
                        No
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="text"
                        @click="confirmRemoveTab(removeTabIndex, true)"
                    >
                        Yes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-form ref="form">
            <v-menu
                v-model="showMenu"
                absolute
                :persistent="!(menuAction !== 'scan')"
                :close-on-content-click="false"
                :disabled="!enableTypeDescriptorPopup"
            >
                <v-card>
                    <v-container v-if="!autoAssign">
                        <v-container>
                            <v-date-input
                                v-model="wellData.collectionDate"
                                label="Sample Collection Date"
                                autofocus
                                @click:clear="clearDate"
                                :clearable="!isAssignBool"
                                :required="isAssignBool"
                                :rules="isAssignBool ? collectionDateRequired : []"
                                :disabled="autoAssign && showPackingDirection"
                            />
                            <v-row class="my-4">
                                <!-- MATERIAL TYPES -->
                                <v-col>
                                    <v-autocomplete
                                        min-width="200px"
                                        v-model="wellData.sampleType"
                                        :items="sampleTypes"
                                        item-title="name"
                                        return-object
                                        :mandatory="this.menuAction === 'edit' ? false : true"
                                        v-if="!autoAssign"
                                        label="Types"
                                        :disabled="autoAssign && showPackingDirection"
                                    ></v-autocomplete>
                                </v-col>
                                <v-divider
                                    v-if="sampleDescriptors.length > 0"
                                    role="presentation"
                                    vertical
                                ></v-divider>
                                <!-- DESCRIPTORS -->
                                <v-col v-if="sampleDescriptors.length > 0">
                                    <v-autocomplete
                                        min-width="200px"
                                        v-model="wellData.sampleDescriptor"
                                        :items="sampleDescriptors"
                                        item-title="name"
                                        return-object
                                        :mandatory="this.menuAction === 'edit' ? false : true"
                                        v-if="!autoAssign"
                                        label="Descriptors"
                                        :disabled="autoAssign && showPackingDirection"
                                    ></v-autocomplete>
                                </v-col>
                                <v-divider
                                    v-if="entityKeys.length > 0"
                                    role="presentation"
                                    vertical
                                ></v-divider>
                                <!-- ENTITY KEYS -->
                                <v-col v-if="entityKeys.length > 0">
                                    <v-autocomplete
                                        min-width="200px"
                                        v-model="wellData.entityKey"
                                        multiple
                                        :items="entityKeys"
                                        item-title="name"
                                        return-object
                                        :mandatory="this.menuAction === 'edit' ? false : true"
                                        v-if="!autoAssign"
                                        label="Target Agent(s)"
                                        :disabled="autoAssign && showPackingDirection"
                                    ></v-autocomplete>
                                </v-col>
                                <v-divider v-if="subwell" role="presentation" vertical></v-divider>
                                <!-- SUBWELLS -->
                                <!-- TODO: how to test subwells? -->
                                <v-col v-if="subwell" class="mt-3 pa-2">
                                    <div class="text-subtitle-2">Subwells</div>
                                    <v-expansion-panels flat multiple>
                                        <v-expansion-panel v-for="(item, i) in subwell" :key="i">
                                            <v-expansion-panel-title>
                                                {{ item.name }}
                                            </v-expansion-panel-title>
                                            <v-expansion-panel-text>
                                                <v-list-group v-model="subwellSelections[item.key]">
                                                    <v-list-item
                                                        v-for="(option, i) in item.values"
                                                        :key="i"
                                                        :modelValue="option"
                                                        :value="true"
                                                        v-on:change="updateSubwells"
                                                    >
                                                        {{ option }}
                                                    </v-list-item>
                                                </v-list-group>
                                            </v-expansion-panel-text>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-col>
                            </v-row>
                            <v-textarea
                                v-model="wellData.note"
                                variant="outlined"
                                hide-details
                                label="Enter a note here"
                            >
                            </v-textarea>
                        </v-container>
                        <v-container v-if="protocols.length > 1" class="py-0">
                            <div class="text-subtitle-2">Select protocol(s)</div>
                            <ul>
                                <li
                                    v-for="protocol in protocols"
                                    :key="protocol.protocol_id"
                                    style="list-style: none"
                                >
                                    <v-tooltip location="right">
                                        <template v-slot:activator="{ props }">
                                            <v-checkbox
                                                class="ma-0"
                                                v-model="selectedProtocols"
                                                :label="protocol.protocol_id"
                                                color="blue"
                                                hide-details
                                                multiple
                                                :modelValue="protocol"
                                                :rules="protocolRequired"
                                                v-bind="props"
                                            ></v-checkbox>
                                        </template>
                                        <span>{{ protocol.name }}</span>
                                    </v-tooltip>
                                </li>
                            </ul>
                            <v-divider role="presentation" horizontal class="mt-8"></v-divider>
                        </v-container>
                        <v-checkbox
                            v-if="this.menuAction === 'scan'"
                            v-model="showPackingDirection"
                            label="Auto-assign on Scan"
                            class="pl-6 ma-0"
                        ></v-checkbox>
                    </v-container>

                    <v-container
                        v-if="
                            this.menuAction === 'assignAll' ||
                            this.menuAction === 'assignMultiple' ||
                            (this.menuAction === 'scan' && this.showPackingDirection)
                        "
                    >
                        <v-divider role="presentation" horizontal></v-divider>
                        <div class="text-subtitle-2">Select plate packing direction:</div>
                        <v-radio-group
                            v-if="this.showPackingDirection"
                            v-model="packingDirection"
                            inline
                            mandatory
                            class="pl-2 ma-0"
                        >
                            <v-radio label="Horizontal" value="horizontal"></v-radio>
                            <v-radio label="Vertical" value="vertical"></v-radio>
                        </v-radio-group>
                        <v-card
                            v-if="this.autoAssign"
                            color="#005a36"
                            theme="dark"
                            max-width="230"
                            class="ma-0"
                        >
                            <v-card-title> Auto-assign Enabled </v-card-title>
                            <v-card-subtitle>
                                Continue scanning to auto-assign samples. Press 'Cancel' when
                                finished.
                            </v-card-subtitle>
                        </v-card>
                    </v-container>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                            @click="closeMenu"
                            :variant="!this.autoAssign ? 'text' : undefined"
                            :color="this.showPackingDirection && this.autoAssign ? 'primary' : null"
                            size="small"
                        >
                            Cancel
                        </v-btn>
                        <!-- *** SAMPLES **** -->
                        <!-- If creating new samples -->
                        <v-btn
                            v-if="
                                this.shouldCreateSamples &&
                                isAssignBool &&
                                !this.showPackingDirection
                            "
                            @click="handleCreateSamples"
                            :disabled="!hasSampleTypeSelection()"
                            color="primary"
                        >
                            Create
                        </v-btn>
                        <!-- If editing existing samples -->
                        <v-btn
                            v-else-if="
                                (this.isSampleBool || this.isAnalyticalBool) && !isAssignBool
                            "
                            @click="handleEditExistingSamples"
                            color="primary"
                        >
                            Assign
                        </v-btn>
                        <!-- *** REQUEST PLATE *** -->
                        <!-- If adding new wells to plate, including with scanner when not AUTOASSIGN -->
                        <v-btn
                            v-else-if="
                                this.menuAction === 'assign' ||
                                (this.menuAction === 'scan' && !showPackingDirection)
                            "
                            @click="handleAssign"
                            color="primary"
                            :disabled="
                                !hasSampleTypeSelection() || this.plateWellSelection.length === 0
                            "
                        >
                            Assign
                        </v-btn>
                        <!-- If adding ALL wells to plate -->
                        <v-btn
                            v-else-if="this.menuAction === 'assignAll'"
                            @click="handleAssignMultiple"
                            color="primary"
                        >
                            Assign All
                        </v-btn>
                        <!-- If adding multiple wells to a plate w/auto-assign behavior -->
                        <v-btn
                            v-else-if="this.menuAction === 'assignMultiple'"
                            @click="handleAssignMultiple"
                            color="primary"
                        >
                            Multi-Assign
                        </v-btn>
                        <!-- If using scanner to AUTOASSIGN to plate -->
                        <v-btn
                            v-else-if="this.menuAction === 'scan' && this.showPackingDirection"
                            @click="confirmAutoAssign"
                            :disabled="autoAssign"
                            color="primary"
                            size="x-small"
                        >
                            Confirm Autoassign
                        </v-btn>
                        <!-- If editing existing wells on plate -->
                        <v-btn
                            v-else
                            color="primary"
                            @click="handleEditExistingWellsOnPlate"
                            :disabled="
                                !hasSampleTypeSelection() ||
                                (this.plateWellSelection.length === 0 && !this.showPackingDirection)
                            "
                        >
                            Assign
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-menu>
        </v-form>
    </v-container>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";
import { EventBus } from "../lib/event-bus";
import { defineComponent, nextTick } from "vue";
import { fetchFermenterRunProtocols, fetchBatchesByCodes } from "@/services/benchling/benchlingAPI";
import { lookupSamplesByExperimentProtocolAndName } from "@/services/samples/samplesAPI";

import BatchList from "../components/Requests/BatchList";
import BatchSearch from "../components/Requests/BatchSearch";
import CreateRequestDialog from "../components/Requests/CreateRequestDialog";
import CreateSamplesDialog from "../components/Samples/CreateSamplesDialog";
import ConditionsList from "../components/Samples/ConditionsList";
import ContainerCard from "../components/Samples/ContainerCard";
import EntityBulkSearch from "@/components/common/EntityBulkSearch";
import ExperimentCard from "../components/Samples/ExperimentCard";
import ExperimentsSearch from "../components/Samples/ExperimentsSearch";
import PlateLayout from "../components/Samples/Plate";
import SampleSelect from "../components/Samples/SampleSelect";
import SubmitDialog from "../components/Samples/SubmitDialog";
import TubeSeries from "../components/Samples/TubeSeries";

export default defineComponent({
    name: "BuildPlate",

    components: {
        BatchList,
        BatchSearch,
        CreateRequestDialog,
        CreateSamplesDialog,
        ConditionsList,
        ContainerCard,
        EntityBulkSearch,
        ExperimentCard,
        ExperimentsSearch,
        PlateLayout,
        SampleSelect,
        SubmitDialog,
        TubeSeries,
    },

    data() {
        return {
            autoAssign: false,
            showPackingDirection: false,
            selectedProtocols: [],
            menuAction: null,
            wellData: {},
            subwellSelections: {},
            showMenu: false,
            activeTabIndex: 0,
            errorMessage: null,
            exp: null,
            isSubmitDialogOpen: false,
            isValid: false,
            radio: "",
            removeTabIndex: undefined,
            showCreateRequestDialog: false,
            showCreateSamplesDialog: false,
            showRemoveTabConfirmDialog: false,
            collectionDatePicker: false,
            collectionDateRequired: [(v) => (v ? true : "Collection Date is required")],
            protocolRequired: [(v) => (v.length > 0 ? true : "Protocol is required")],
            packingDirection: null,
            clear: false,
            fermenterRunProtocols: [],
            selectedFermenterRunBatches: [],
        };
    },

    computed: {
        ...mapGetters("plateCreate", [
            "activePlate",
            "initialized",
            "isMultiAssign",
            "isPlateValid",
            "isTubeSeries",
            "plates",
            "request",
            "plateWellSelection",
            "sampleTypes",
            "sampleDescriptors",
            "entityKeys",
            "subwell",
            "activeCondition",
            "conditionMetadata",
            "selectionHasCondition",
            "experiment",
            "sampleConditionSelection",
            "protocols",
            "isProcessing",
            "sampleEditSelection",
        ]),
        noPlatesMessage() {
            return this.isMultiAssign
                ? "Select an experiment and pick or scan your first sample"
                : "Click the + icon above to create a plate.";
        },
        isSampleBool() {
            return this.$route.query.type === "sample";
        },
        isAssignBool() {
            return (
                this.menuAction === "assign" ||
                this.menuAction === "assignAll" ||
                this.menuAction === "scan" ||
                this.menuAction === "create" ||
                this.menuAction === "assignMultiple"
            );
        },
        shouldCreateSamples() {
            return this.isTubeSeries || this.isSampleBool;
        },
        isAnalyticalBool() {
            return (
                this.protocols[0] &&
                this.protocols[0].functional_team === "Analytical" &&
                this.protocols[0].sample_workflow !== "Analytical - SDS-PAGE" &&
                this.protocols[0].sample_workflow !== "Analytical - Plate Reader"
            );
        },
        isFermenterRunBatchSelected() {
            const fermenterRunOptions = ["fermenter", "mda", "protein"];
            return (
                fermenterRunOptions.includes(this.radio) &&
                this.selectedFermenterRunBatches.length > 0
            );
        },
        enableAssignButton() {
            const samplesSelected = this.sampleConditionSelection.length > 0;
            const plateWellsSelected = this.plateWellSelection.length > 0;
            if (this.isFermenterRunBatchSelected && plateWellsSelected) {
                return true;
            }

            if (this.isSampleBool) {
                return this.radio === "condition" ? samplesSelected : this.activeCondition;
            } else if (!this.isTubeSeries) {
                return samplesSelected && plateWellsSelected;
            } else {
                return samplesSelected || this.activeCondition;
            }
        },
        isFermenterRunExperiment() {
            return (
                this.exp &&
                this.fermenterRunProtocols.find(
                    (protocol) => protocol.protocol_id === this.exp.protocol_id
                )
            );
        },
        hasActiveCondition() {
            return this.activeCondition || this.sampleConditionSelection.length > 0;
        },
    },

    async created() {
        const requestCode = this.$route.params.requestCode;
        if (requestCode) {
            try {
                await this.edit(requestCode);
            } catch (err) {
                console.error(`An error occurred fetching ${requestCode}`, err);
                const msg = _.get(err, "response.data.error.message") || err.message;
                EventBus.$emit("globalAlert", `An error happened loading the request: ${msg}`);
            }
        } else {
            if (this.isSampleBool) {
                this.showCreateSamplesDialog = true;
            } else {
                this.showCreateRequestDialog = true;
            }
        }
        this.fermenterRunProtocols = await fetchFermenterRunProtocols();
    },

    unmounted() {
        this.reset();
    },

    watch: {
        exp: {
            handler: function (val) {
                this.clearMetadata();
                if (this.isFermenterRunExperiment) {
                    this.radio = "fermenter";
                } else {
                    this.radio = "condition";
                }
            },
            deep: true,
        },
    },

    methods: {
        ...mapActions("plateCreate", [
            "addTab",
            "edit",
            "initialize",
            "removeTab",
            "reset",
            "setActivePlate",
            "submit",
            "assignToSelection",
            "removeSample",
            "removeFromSelection",
            "setExistingPlates",
            "expandPlateSelection",
            "addSelectedSamples",
            "clearSampleConditions",
            "clearSampleConditionsAndMetadata",
            "editSampleSelection",
            "assignAllWellsToPlate",
            "assignScannedSampleToPlate",
            "setActiveCondition",
            "setSampleConditionSelection",
        ]),
        ...mapActions("requests", ["addRequest"]),
        onInitialize(initData) {
            this.exp = null;
            this.initialize(initData);
        },
        onProceed() {
            this.isSubmitDialogOpen = true;
        },
        async onSubmit(data) {
            try {
                this.errorMessage = null;
                const responseArray = await this.submit(data);
                this.isSubmitDialogOpen = false;
                EventBus.$emit(
                    "globalAlert",
                    `Request ${_.map(responseArray, "entityRegistryId").join(", ")} was successfully created (may take up to a minute to appear).`
                );
                if (this.isSampleBool) {
                    await this.$router.push({ name: "DisplaySamples" });
                }
            } catch (err) {
                console.error("An error happened", err);
                const message = _.get(err, "response.data.error.message") || err.message;
                this.errorMessage = message || "An error happened saving the request.";
            }
        },
        onCancel() {
            this.reset();
            this.$router.push({ name: "DisplayRequests" });
        },
        remove() {
            if (this.isSampleBool || this.isAnalyticalBool) {
                this.removeSample();
            } else {
                this.removeFromSelection();
            }
        },
        removeTabDialog(index) {
            this.removeTabIndex = index;
            this.showRemoveTabConfirmDialog = true;
        },
        confirmRemoveTab(index, confirmed) {
            if (confirmed) {
                this.removeTab(index);
            }
            this.showRemoveTabConfirmDialog = false;
        },
        autoShowTypeDescriptorPopup: function () {
            return this.sampleTypes.length > 1 && this.hasActiveCondition;
        },
        enableTypeDescriptorPopup: function () {
            return (
                (this.sampleTypes.length > 1 || this.sampleDescriptors.length > 1) &&
                this.hasActiveCondition
            );
        },
        hasSampleTypeSelection: function () {
            return this.sampleTypes.length > 0;
        },
        clearDate: function () {
            this.wellData.collectionDate = null;
        },
        clearMetadata() {
            this.wellData = {};
            this.subwellSelections = {};
            this.setActiveCondition(null);
            this.clearSampleConditionsAndMetadata();
        },
        openMenu(action) {
            this.menuAction = action;
            // If editing, expand selection to include all samps with identical id's to those in the selection
            if (action === "edit" && !this.isSampleBool && !this.isAnalyticalBool) {
                this.expandPlateSelection(this.exp.exp);
                if (this.plateWellSelection.length === 0) {
                    nextTick(() => {
                        this.closeMenu();
                    });
                }
            }
            if (this.radio === "sample" && this.activeCondition && action !== "edit") {
                // Convert sampleType to sampleType object {name: XXXX, id: XXXX}
                if (this.activeCondition.sample_type) {
                    this.wellData.sampleType = this.sampleTypes.find((obj) => {
                        return obj.name === this.activeCondition.sample_type;
                    });
                }

                // Map sampleDesciptor to sampleDescriptor object {name: XXXX, id: XXXX}
                if (this.activeCondition.sample_descriptor) {
                    this.wellData.sampleDescriptor = this.activeCondition.sample_descriptor.map(
                        (descriptor) => {
                            return this.sampleDescriptors.find((obj) => {
                                return obj.name === descriptor;
                            });
                        }
                    );
                    this.wellData.sampleDescriptor = _.uniq(this.wellData.sampleDescriptor);
                }

                this.wellData.collectionDate = this.activeCondition.collection_date;
                this.wellData.note = this.activeCondition.note;
                if (this.activeCondition.subwell) {
                    this.wellData.subwell = this.activeCondition.subwell;
                    this.subwellSelections = this.activeCondition.subwell;
                }
            } else if (this.conditionMetadata && action !== "edit") {
                this.wellData.sampleType = this.conditionMetadata.sampleType;
                this.wellData.sampleDescriptor = _.uniq(this.conditionMetadata.sampleDescriptor);
                this.wellData.collectionDate = this.conditionMetadata.collectionDate;
                this.wellData.note = this.conditionMetadata.note;
                this.wellData.subwell = this.conditionMetadata.subwell;
                if (this.conditionMetadata.subwell) {
                    this.subwellSelections = this.conditionMetadata.subwell;
                } else {
                    this.subwellSelections = {};
                }
            }

            if (action === "assignAll" || action === "assignMultiple") {
                this.showPackingDirection = true;
            }

            // Clear sampleDescriptors when editing samples to prevent assigning multiple
            if (this.isSampleBool && action === "edit") {
                this.wellData.sampleDescriptor = [];
            }
            this.selectedProtocols = this.protocols;
            this.showMenu = true;
        },
        closeMenu() {
            this.wellData = {};
            this.subwellSelections = {};
            this.showMenu = false;
            this.showPackingDirection = false;
            this.menuAction = null;
            this.autoAssign = false;
            this.clearSampleConditions();
        },
        // This is called for the "Assign" workflow when not editing
        handleAssign() {
            if (this.$refs.form.validate()) {
                this.assignToSelection(this.wellData);
                this.closeMenu();
            }
        },
        // This is called for the "Edit" workflow for existing wells on a plate
        // TODO: why doesn't this validate even though the create/assign codepaths do?
        handleEditExistingWellsOnPlate() {
            this.wellData.action = "edit";
            this.assignToSelection(this.wellData);
            this.closeMenu();
        },
        // This is called for the "Create" workflows
        handleCreateSamples() {
            if (this.$refs.form.validate()) {
                this.addSelectedSamples({
                    selectedProtocols: this.selectedProtocols,
                    wellData: this.wellData,
                });
                this.closeMenu();
            }
        },
        // This is called for the "Edit" workflow when editing existing samples
        // TODO: why doesn't this validate even though the create/assign codepaths do?
        handleEditExistingSamples() {
            this.wellData.protocols = this.selectedProtocols;
            this.editSampleSelection(this.wellData);
            this.closeMenu();
        },
        onOpenOptionsMenu(action) {
            if (action === "scan" && this.autoAssign) {
                this.setActiveCondition(_.merge(this.activeCondition, this.wellData));
                this.assignScannedSampleToPlate(this.packingDirection);
            } else {
                this.menuAction = action;
                this.openMenu(action);
            }
        },
        // This is called for the "Assign All" workflow AND the "Multi-Assign" workflow
        handleAssignMultiple() {
            if (this.$refs.form.validate()) {
                this.wellData.packingDirection = this.packingDirection;
                this.assignAllWellsToPlate(this.wellData);
                this.closeMenu();
            }
        },
        confirmAutoAssign() {
            this.autoAssign = true;
        },
        updateSubwells() {
            nextTick(() => {
                Object.keys(this.subwellSelections).forEach((key) => {
                    if (!this.subwellSelections[key]) {
                        delete this.subwellSelections[key];
                    }
                });
                this.wellData.subwell = this.subwellSelections;
            });
        },
        onBatchesSelected(batches) {
            this.selectedFermenterRunBatches = batches;
        },
        async fetchBatches(batches) {
            return fetchBatchesByCodes(batches);
        },
        async fetchSamples(samples) {
            return lookupSamplesByExperimentProtocolAndName(
                this.exp.exp,
                this.protocols[0].protocol_id,
                samples
            );
        },
        onBatchesBulkSelected(lookupResult) {
            this.handleBulkSelect(lookupResult.entities, "Batch");
        },
        onSamplesBulkSelected(lookupResult) {
            this.handleBulkSelect(lookupResult.entities, "Sample");
        },
        handleBulkSelect(entities, type) {
            if (entities.length > 0) {
                entities.forEach((entity) => {
                    entity.type = type;
                    entity.name = entity.entity_name;
                    entity.experiment_code = entity.experiment_code || this.exp.exp;
                    entity.experiment_id = entity.experiment_id || this.exp.id;
                });
                this.setActiveCondition(entities[0]);
                this.setSampleConditionSelection(entities);
            } else {
                this.setActiveCondition(null);
                this.setSampleConditionSelection([]);
            }
        },
    },
});
</script>

<style scoped></style>
